import { useMutation, useQuery } from "@tanstack/react-query"
import globalAxios from "axios"
import { queryClient } from "@/lib/queryClient"
import { BASE_PATH, meterCatalogApi } from "@/lib/api"
import type {
  ApiResponseListMeterCatalogItemDTO,
  ApiResponseMeterCatalogItemDTO,
  ListBrandsScopeEnum,
  ListHardwareScopeEnum,
  MeterCatalogItemDTO,
  MeterInfoBrandRequest,
  MeterInfoHardwareRequest,
} from "@/gen/api"

const QUERY_KEY = "meter-info-admin"

type MeterInfoHardwareConvertToGlobalRequest = {
  sourceGlobalHardwareId: number
}

const unwrapList = (response: { data: ApiResponseListMeterCatalogItemDTO }) =>
  (response.data.data ?? []) as MeterCatalogItemDTO[]
const unwrapItem = (response: { data: ApiResponseMeterCatalogItemDTO }) =>
  response.data.data as MeterCatalogItemDTO

export const useMeterInfoBrands = (scope?: ListBrandsScopeEnum, councilCode?: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, "brands", scope, councilCode],
    queryFn: async () => {
      const response = await meterCatalogApi.listBrands(scope, councilCode?.trim() || undefined)
      return unwrapList(response)
    },
  })
}

export const useMeterInfoHardware = (
  scope?: ListHardwareScopeEnum,
  councilCode?: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, "hardware", scope, councilCode],
    enabled,
    queryFn: async () => {
      const response = await meterCatalogApi.listHardware(scope, councilCode?.trim() || undefined)
      return unwrapList(response)
    },
  })
}

const invalidateMeterInfo = () => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
  queryClient.invalidateQueries({ queryKey: ["meter-info-snapshot"] })
}

export const useCreateMeterInfoBrand = () =>
  useMutation({
    mutationFn: async (request: MeterInfoBrandRequest) =>
      unwrapItem(await meterCatalogApi.createBrand(request)),
    onSuccess: invalidateMeterInfo,
  })

export const useUpdateMeterInfoBrand = () =>
  useMutation({
    mutationFn: async ({ id, request }: { id: number; request: MeterInfoBrandRequest }) =>
      unwrapItem(await meterCatalogApi.updateBrand(id, request)),
    onSuccess: invalidateMeterInfo,
  })

export const useDeactivateMeterInfoBrand = () =>
  useMutation({
    mutationFn: async (id: number) => unwrapItem(await meterCatalogApi.deactivateBrand(id)),
    onSuccess: invalidateMeterInfo,
  })

export const useCreateMeterInfoHardware = () =>
  useMutation({
    mutationFn: async (request: MeterInfoHardwareRequest) =>
      unwrapItem(await meterCatalogApi.createHardware(request)),
    onSuccess: invalidateMeterInfo,
  })

export const useUpdateMeterInfoHardware = () =>
  useMutation({
    mutationFn: async ({ id, request }: { id: number; request: MeterInfoHardwareRequest }) =>
      unwrapItem(await meterCatalogApi.updateHardware(id, request)),
    onSuccess: invalidateMeterInfo,
  })

export const useConvertMeterInfoHardwareToGlobal = () =>
  useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: number
      request: MeterInfoHardwareConvertToGlobalRequest
    }) =>
      unwrapItem(
        await globalAxios.post(
          `${BASE_PATH}/api/v1/meter-catalog/admin/hardware/${id}/convert-to-global`,
          request,
        ),
      ),
    onSuccess: invalidateMeterInfo,
  })

export const useDeactivateMeterInfoHardware = () =>
  useMutation({
    mutationFn: async (id: number) => unwrapItem(await meterCatalogApi.deactivateHardware(id)),
    onSuccess: invalidateMeterInfo,
  })
